// models/api/channels.ts
import { deleteRequest, getRequest, postRequest } from './rest'
import { ALL_ORGS_SELECTED } from 'lib/constants'
import type { Channel } from 'lib/types/channels'

const orgHeader = (orgId: string) =>
  orgId === ALL_ORGS_SELECTED ? {} : { org_id: orgId }

// Build a stable id if backend doesn't provide one
const buildId = (c: any) => {
  const raw =
    c?.id ??
    c?.uuid ??
    c?.channel_id ??
    // fallback: deterministic composite (org + name)
    `${c?.org_id ?? c?.orgId ?? 'no-org'}:${c?.name ?? 'no-name'}`
  return String(raw)
}

const normalize = (c: any): Channel => ({
  ...c,
  id: buildId(c),
  orgId: c.org_id ?? c.orgId ?? '',
})

export const getChannels = async (orgId: string): Promise<Channel[]> => {
  const res = await getRequest({ endpoint: 'api/v5/channels', header: orgHeader(orgId) })
  const list = res?.data?.data ?? []
  return list.map(normalize)
}

// models/api/channels.ts
export const createChannel = async (name: string, orgId: string): Promise<Channel> => {
  const res = await postRequest({
    endpoint: 'api/v5/channels',
    header: orgHeader(orgId),
    data: { name, org_id: orgId },
  })
  return normalize(res?.data)
}


/** POST /api/v5/channels/add_user  ->  204 (no content) */
export const addUserToChannel = async (
  userId: string,
  channelName: string,
  orgId: string,
): Promise<void> => {
  try {
    await postRequest({
      endpoint: 'api/v5/channels/add_user',
      header: orgHeader(orgId),
      // Swagger body:
      data: { channel_name: channelName, org_id: orgId, user_id: userId },
    })
  } catch (err) {
    console.error('Error adding user to channel:', { userId, channelName, orgId, err })
    throw err
  }
}

/** POST /api/v5/channels/remove_user  ->  204 (no content) */
export const removeUserFromChannel = async (
  userId: string,
  channelName: string,
  orgId: string,
): Promise<void> => {
  try {
    await postRequest({
      endpoint: 'api/v5/channels/remove_user',
      header: orgHeader(orgId),
      data: { channel_name: channelName, org_id: orgId, user_id: userId },
    })
  } catch (err) {
    console.error('Error removing user from channel:', { userId, channelName, orgId, err })
    throw err
  }
}

/**
 * ✅ Delete a channel
 * Swagger: DELETE /api/v5/channels/{id}
 * Body: { channel_name, org_id }
 */
export async function deleteChannel(channelId: string, channelName: string, orgId: string) {
  const response = await deleteRequest({
    endpoint: `api/v5/channels/${channelName}`,
    header: { org_id: orgId },
    data: {
      org_id: orgId,
    },
  })
  return response?.data
}
